import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Modal, Input, Select, Loading } from '../components/ui';

export default function FundGroupMaster() {
  const [groups, setGroups] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDivision, setFilterDivision] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', division_id: '' });
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Fund. operation master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterDivision ? { division_id: filterDivision } : {};
      const [grps, divs] = await Promise.all([
        api.get('/fundamental-groups', { params }),
        api.get('/divisions')
      ]);
      setGroups(grps.data);
      setDivisions(divs.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterDivision]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', division_id: filterDivision || '' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, description: g.description || '', division_id: g.division_id || '' });
    setError(''); setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { setError('Name is required'); return; }
    try {
      const payload = { ...form, division_id: form.division_id || null };
      if (editing) await api.put(`/fundamental-groups/${editing.id}`, payload);
      else await api.post('/fundamental-groups', payload);
      setModalOpen(false); setForm({ name: '', description: '', division_id: '' }); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/fundamental-groups/${deleteTarget.id}`);
      setDeleteTarget(null); fetchAll();
    } catch (e) {
      setDeleteError(e.response?.data?.error || 'Error deleting');
    }
  };

  const colorMap = {
    Hem: '#EAF3DE', Attach: '#FAEEDA', Topstitch: '#F1EFE8',
    'Edge stitch': '#F1EFE8', Folder: '#F1EFE8', Profile: '#E6F1FB'
  };
  const textMap = {
    Hem: '#27500A', Attach: '#633806', Topstitch: '#444441',
    'Edge stitch': '#444441', Folder: '#444441', Profile: '#0C447C'
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Fundamental operation master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{groups.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Groups operations by fundamental skill type for cross-skill matching</div>
        </div>
        <Select value={filterDivision} onChange={setFilterDivision} placeholder="All divisions"
          options={divisions.map(d => ({ value: d.id, label: d.name }))} />
        <Btn variant="primary" size="sm" onClick={openAdd}>+ Add group</Btn>
      </div>

      {loading ? <Loading /> : groups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 13 }}>
          No groups found
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
          {groups.map(g => (
            <div key={g.id} style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{
                  background: colorMap[g.name] || '#F1EFE8',
                  color: textMap[g.name] || '#444441',
                  padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 500
                }}>{g.name}</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(g)}>Edit</a>
                  <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }}
                    onClick={() => { setDeleteTarget(g); setDeleteError(''); }}>Remove</a>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>{g.description}</div>
              {g.division_name && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Division: <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{g.division_name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit fundamental group' : 'Add fundamental group'} width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Group name</div>
            <Input value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))}
              placeholder="e.g. Collar attach" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Description</div>
            <textarea className="inp" value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Describe what operations belong to this group…"
              style={{ width: '100%', height: 72, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division</div>
            <Select value={form.division_id} onChange={v => setForm(p => ({ ...p, division_id: v }))}
              placeholder="Select division…" options={divisions.map(d => ({ value: d.id, label: d.name }))}
              style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove group" width={340}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13 }}>
            Remove <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Groups linked to existing operations cannot be removed.
          </div>
          {deleteError && (
            <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>
              {deleteError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setDeleteTarget(null)}>Cancel</Btn>
            <Btn variant="primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={handleDelete}>Remove</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}